import {
  Box,
  Button,
  FormControl,
  Modal,
  Stack,
  TextField,
  Typography,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { BrickManagementStore } from "../../../../../../store/main/BrickManagementStore";
import { BricksType } from "../../../../../../domain/models/brick-management-report/brick-management-data";
import { useEffect, useState } from "react";
import { FetchState } from "../../../../../../domain/models/fetch-state";
import { TerritoryDataStore } from "../../../../../../store/main/TerritoryDataStore";
import { updateBrickValidation } from "../../../../../../common/validations/validations";
import { locality } from "../../../../../../common/constants/constant";

interface Props {
  open: boolean;
  handleClose: Function;
  successCallback: Function;
  row: BricksType;
  hq: string;
  brick_id: number;
  hq_id: number;
}
interface Payload {
  hq_headquarter_id: number;
  no_of_work_days: number;
  expected_business: number;

  locality: string;
  distance_from_hq: string | number;
  station: string;
  distance_from_base_brick: string | number;
  base_brick_id?: string | null;
  reason: string;
}

type FormFields = {
  brick: string;
  distance: string;
  station: string;
  expected: string;
  locality: string;
  headquarter: string;
  workdays: string;
  reason: string;
  otherReason: string;
  baseBrick: string;
  distanceFromBaseBrick: string;
};

interface Brick {
  id: number;
  name: string;
  brick_code: string;
  station: string;
}

const UpdateBrickModal: React.FC<Props> = ({
  open,
  handleClose,
  successCallback,
  row,
  hq,
  brick_id,
  hq_id,
}) => {
  const {
    fetchDistrictState,
    fetchCluster,
    districtData,
    clusterData,
    updateBrick,
    BrickManagementData,
    fetchBaseBrickLocation,
    FetchBaseBrickLocationState,
    FetchBaseBrickLocation,
  } = BrickManagementStore();

  const { fetchBricksMaster, bricksMasterData } = TerritoryDataStore();

  const [brickData, setBrickData] = useState<Brick[]>([]);
  const { handleSubmit, control, watch, setValue } = useForm<FormFields>({
    mode: "onChange",
  });

  const selectedStation = watch("station");
  const baseBrick = watch("baseBrick");
  const distanceFromBaseBrick = watch("distanceFromBaseBrick");
  const distance = watch("distance");

  const hasHQBrick = brickData.some((brick) => brick.station === "HQ");

  const fetchBaseBrickLocationData = async (station: string) => {
    const params = { station, headquarter_ids: [hq_id] };

    try {
      const response = await fetchBaseBrickLocation(params);

      if (!response || !response.bricks || !Array.isArray(response.bricks)) {
        console.error("Invalid API response:", response);
        setBrickData([]);
        return;
      }
      setBrickData(response?.bricks || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setBrickData([]);
    }
  };

  useEffect(() => {
    if (selectedStation === "HQ") {
      setValue("baseBrick", "Self");
      setValue("distanceFromBaseBrick", "0");
      setValue("distance", "0");
    } else if (selectedStation === "OS") {
      setValue("distance", distanceFromBaseBrick);
    } else if (selectedStation === "EX" && baseBrick) {
      const selectedBrick = brickData.find(
        (brick) => brick.id === Number(baseBrick)
      );
      if (selectedBrick?.station === "HQ") {
        setValue("distance", distanceFromBaseBrick);
      }
    }
  }, [selectedStation, baseBrick, distanceFromBaseBrick]);

  const handleUpdate = () => {
    const payload: Payload = {
      hq_headquarter_id: hq_id,
      no_of_work_days: parseInt(watch("workdays")),
      expected_business: parseInt(watch("expected")),
      locality: watch("locality"),
      distance_from_hq: watch("distance"),
      station: watch("station"),
      distance_from_base_brick: distanceFromBaseBrick,
      reason:
        watch("reason") === "Other" ? watch("otherReason") : watch("reason"),
    };

    if (watch("baseBrick") !== "Self") {
      payload.base_brick_id = watch("baseBrick");
    }

    updateBrick(brick_id, payload);
    successCallback();
    handleClose();
  };

  useEffect(() => {
    if (
      row.bricks.distance_from_hq !== null &&
      watch("station") === row.bricks.station
    ) {
      setValue("distance", distanceFromBaseBrick);
    }

    if (
      parseInt(watch("baseBrick")) === row.brick_id
      // watch("baseBrick") === "Self"
    ) {
      setValue("distanceFromBaseBrick", "0");
    }
  }, [watch("station"), watch("baseBrick")]);

  useEffect(() => {
    if (selectedStation) {
      fetchBaseBrickLocationData(selectedStation);
      // setValue("baseBrick", ""); // Reset baseBrick when station changes
    }
  }, [selectedStation]);

  const totalWorkingDaysCheck =
    BrickManagementData[0].regions[0].areas[0].headquarters.reduce(
      (acc, value) => acc + value.total_no_of_work_days,
      0
    );

  return (
    <Modal open={open} onClose={() => handleClose()}>
      <Box
        position={"absolute" as "absolute"}
        top={"50%"}
        left={"50%"}
        bgcolor={"background.paper"}
        borderRadius={"22px"}
        sx={{ transform: "translate(-50%, -50%)" }}
        width={"25%"}
        p={3}
      >
        <Typography
          fontWeight={500}
          variant="h6"
          textAlign="center"
          marginBottom={2}
        >
          Update Brick
        </Typography>
        <Stack spacing={2}>
          <Controller
            name="brick"
            control={control}
            defaultValue={row.brick}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl size="small" fullWidth>
                <TextField
                  id="outlined-disabled"
                  label="Brick Name"
                  value={value}
                  fullWidth
                  size="small"
                  disabled
                />
              </FormControl>
            )}
          />
          <Controller
            name="headquarter"
            defaultValue={hq}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl size="small" fullWidth>
                <TextField
                  id="outlined-disabled"
                  label="Headquarter Name"
                  value={value}
                  fullWidth
                  size="small"
                  disabled
                />
              </FormControl>
            )}
          />
          {/* <Controller
            name="workdays"
            control={control}
            defaultValue={row.bricks.no_of_work_days?.toString()}
            rules={{
              ...updateBrickValidation.workDays,
              validate: (newWorkDays) => {
                const numWorkDays = Number(newWorkDays);
                const originalWorkDays = row.bricks.no_of_work_days;
                const adjustedTotalWorkDays =
                  totalWorkingDaysCheck - originalWorkDays + numWorkDays;

                if (!numWorkDays) return "No of Work Days is required";
                if (numWorkDays === 0) return "Work Days cannot be 0";
                if (numWorkDays > 3)
                  return "Working Days should not exceed beyond 3 days";
                // if (adjustedTotalWorkDays > 24) {
                //   return "Your working days limit of 24 is reached";
                // }
                return true;
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const numWorkDays = Number(value);
              const originalWorkDays = row.bricks.no_of_work_days;
              const adjustedTotalWorkDays =
                totalWorkingDaysCheck -
                originalWorkDays +
                (isNaN(numWorkDays) ? 0 : numWorkDays);

              return (
                <FormControl size="small" fullWidth>
                  <TextField
                    fullWidth
                    value={value}
                    id="outlined-required"
                    label="No of Work Days"
                    size="small"
                    onChange={(event) => onChange(event.target.value)}
                    error={!!error}
                    helperText={error?.message}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color:
                        adjustedTotalWorkDays > 24 ? "error.main" : "#42a5f5", // Light blue if ≤ 24, red if > 24
                    }}
                  >
                    Total Working Days: {adjustedTotalWorkDays}
                  </Typography>
                </FormControl>
              );
            }}
          /> */}

          <Controller
            name="workdays"
            control={control}
            defaultValue={row.bricks.no_of_work_days?.toString()}
            rules={{
              validate: (newWorkDays) => {
                const numWorkDays = Number(newWorkDays);
                const originalWorkDays = row.bricks.no_of_work_days;
                const adjustedTotalWorkDays =
                  totalWorkingDaysCheck - originalWorkDays + numWorkDays;

                if (!numWorkDays) return "No of Work Days is required";
                if (numWorkDays === 0) return "Work Days cannot be 0";

                if (totalWorkingDaysCheck > 24) {
                  
                  if (numWorkDays >= originalWorkDays) {
                    return "Your working days are already above 24, you can only decrease.";
                  }
                } else {
                 
                  if (adjustedTotalWorkDays > 24) {
                    return "Your working days limit of 24 is reached.";
                  }
                }

                return true;
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const numWorkDays = Number(value);
              const originalWorkDays = row.bricks.no_of_work_days;
              const adjustedTotalWorkDays =
                totalWorkingDaysCheck -
                originalWorkDays +
                (isNaN(numWorkDays) ? 0 : numWorkDays);

              return (
                <FormControl size="small" fullWidth>
                  <TextField
                    fullWidth
                    value={value}
                    id="outlined-required"
                    label="No of Work Days"
                    size="small"
                    onChange={(event) => onChange(event.target.value)}
                    error={!!error}
                    helperText={error?.message}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color:
                        adjustedTotalWorkDays > 24 ? "error.main" : "#42a5f5",
                    }}
                  >
                    Total Working Days: {adjustedTotalWorkDays}
                  </Typography>
                </FormControl>
              );
            }}
          />

          <Stack direction={"row"} gap={2}>
            <Controller
              name="station"
              control={control}
              defaultValue={row.bricks.station ? row.bricks.station : ""}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl fullWidth size="small">
                  <InputLabel id="demo-simple-select-label">
                    EX/OS/HQ
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="select"
                    value={value}
                    onChange={onChange}
                    displayEmpty
                    label="EX/OS/HQ"
                  >
                    <MenuItem value="EX">EX</MenuItem>
                    <MenuItem value="OS">OS</MenuItem>
                    <MenuItem value="HQ">HQ</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name="distance"
              control={control}
              defaultValue={watch("station") === "HQ" ? "0" : ""}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl size="small" fullWidth>
                  <TextField
                    fullWidth
                    focused
                    id="outlined-required"
                    label="Distance from HQ"
                    value={
                      selectedStation === "HQ"
                        ? "0"
                        : baseBrick &&
                          brickData.find(
                            (brick) => brick.id === Number(baseBrick)
                          )?.station === "HQ"
                        ? distanceFromBaseBrick
                        : value
                    }
                    size="small"
                    disabled={
                      selectedStation === "HQ" ||
                      !!(
                        baseBrick &&
                        brickData.find(
                          (brick) => brick.id === Number(baseBrick)
                        )?.station === "HQ"
                      )
                    }
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                  />
                </FormControl>
              )}
              rules={updateBrickValidation.distance}
            />
          </Stack>
          <Stack direction={"row"} gap={2}>
            {selectedStation && selectedStation === "HQ" ? (
              <TextField
                fullWidth
                label="Base Brick"
                value="Self"
                disabled
                InputProps={{ style: { height: 40 } }}
                InputLabelProps={{
                  shrink: selectedStation === "HQ" ? true : undefined,
                }}
              />
            ) : (
              <Controller
                name="baseBrick"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <FormControl
                    fullWidth
                    size="small"
                    error={selectedStation === "OS" && !hasHQBrick}
                  >
                    <InputLabel>Base Brick</InputLabel>
                    <Select value={value} onChange={onChange}>
                      {brickData.map((brick) => (
                        <MenuItem key={brick.id} value={brick.id}>
                          {brick.name} -{" "}
                          <Typography
                            component="span"
                            fontWeight="bold"
                            sx={{
                              backgroundColor: "#f0f0f0",
                              px: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            {brick.station}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Select>
                    {selectedStation === "OS" && !hasHQBrick && (
                      <FormHelperText>
                        Please add an HQ location first
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            )}

            <Controller
              name="distanceFromBaseBrick"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl size="small" fullWidth>
                  <TextField
                    fullWidth
                    id="outlined-required"
                    label="Distance from Base Brick"
                    value={value}
                    size="small"
                    disabled={selectedStation === "HQ"}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    InputLabelProps={{ shrink: true }}
                  />
                </FormControl>
              )}
              rules={updateBrickValidation.distanceFromBaseBrick}
            />
          </Stack>
          <Stack direction={"row"} gap={2}>
            <Controller
              name="expected"
              control={control}
              defaultValue={
                row.bricks.expected_business != null
                  ? row.bricks.expected_business.toString()
                  : ""
              }
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl size="small" fullWidth>
                  <TextField
                    fullWidth
                    id="outlined-required"
                    label="Expected Business"
                    value={value}
                    size="small"
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CurrencyRupeeIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              )}
              rules={updateBrickValidation.expectedBusiness}
            />
            <Controller
              name="locality"
              control={control}
              defaultValue={
                row.bricks.locality ? row.bricks.locality.toString() : ""
              }
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl fullWidth size="small">
                  <InputLabel id="demo-simple-select-label">
                    Locality
                  </InputLabel>
                  <Select
                    labelId="demo-simple-select-label"
                    id="select"
                    value={value}
                    onChange={onChange}
                    displayEmpty
                    label="Locality"
                  >
                    <MenuItem value="metro">{locality.METRO}</MenuItem>
                    <MenuItem value="semi_metro">
                      {locality.SEMI_METRO}
                    </MenuItem>
                    <MenuItem value="others">{locality.OTHERS}</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Stack>
          <Controller
            name="reason"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl fullWidth size="small">
                <InputLabel id="demo-simple-select-label">Reason</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="select"
                  value={value}
                  onChange={onChange}
                  displayEmpty
                  label="Reason"
                >
                  <MenuItem value="Update Brick">Update Brick</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            )}
          />
          {watch("reason") === "Other" && (
            <Controller
              name="otherReason"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl size="small" fullWidth>
                  <TextField
                    fullWidth
                    id="outlined-required"
                    label="Other Reason"
                    value={value}
                    size="small"
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message}
                  />
                </FormControl>
              )}
              rules={updateBrickValidation.otherReason}
            />
          )}
        </Stack>

        <Stack
          paddingTop={3}
          direction={"row"}
          gap={3}
          justifyContent={"center"}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(handleUpdate)}
            disabled={
              !(
                watch("brick") &&
                watch("headquarter") &&
                watch("distance") &&
                watch("station") &&
                watch("expected") &&
                watch("distanceFromBaseBrick") !== "" && 
                watch("distance") !== "" && 
                ((watch("reason") && watch("reason") !== "Other") ||
                  (watch("reason") === "Other" &&
                    watch("otherReason") &&
                    watch("otherReason").length > 4))
              )
            }
            sx={{
              textTransform: "unset",
              borderRadius: "30px",
              minWidth: "30%",
            }}
          >
            Update
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => handleClose()}
            sx={{
              textTransform: "unset",
              borderRadius: "30px",
              minWidth: "30%",
            }}
          >
            Cancel
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

export default UpdateBrickModal;
